import React, { useEffect, useState } from 'react';
import {
  getProfile,
  updateMarketingConsent,
  updateProfileDetails,
} from '../api/profile';
import { MARKETING_CONSENT_VERSION } from '../consent/versions';
import { PROFILE_COUNTRIES } from '../data/countries';
import { PROFILE_PARTICIPANT_ROLES } from '../data/profileRoles';
import PhiAttestationSection from './PhiAttestationSection';

export default function AccountPreferences({ onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [marketing, setMarketing] = useState(false);
  const [organization, setOrganization] = useState('');
  const [role, setRole] = useState('');
  const [country, setCountry] = useState('');
  const [phiRequired, setPhiRequired] = useState(true);
  const [savingMarketing, setSavingMarketing] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [error, setError] = useState(null);
  const [profileSaved, setProfileSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const profile = await getProfile();
        if (cancelled) return;
        setMarketing(Boolean(profile.marketing_consent_firstparty));
        setOrganization(profile.organization || '');
        setRole(profile.role || '');
        setCountry(profile.country || '');
        setPhiRequired(Boolean(profile.phi_attestation_required));
      } catch (err) {
        if (!cancelled) setError(err?.code || err?.message || 'profile_unavailable');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleMarketingToggle(next) {
    setSavingMarketing(true);
    setError(null);
    try {
      const profile = await updateMarketingConsent(next);
      setMarketing(Boolean(profile.marketing_consent_firstparty));
      if (typeof onSaved === 'function') onSaved();
    } catch (err) {
      setError(err?.code || err?.message || 'marketing_consent_update_failed');
      setMarketing((prev) => prev);
    } finally {
      setSavingMarketing(false);
    }
  }

  async function handleSaveProfile(event) {
    event.preventDefault();
    setError(null);
    setProfileSaved(false);
    if (!organization.trim() || !role || !country) {
      setError('Organization, role, and country are required.');
      return;
    }
    setSavingProfile(true);
    try {
      await updateProfileDetails({
        organization: organization.trim(),
        role,
        country,
      });
      setProfileSaved(true);
      if (typeof onSaved === 'function') onSaved();
    } catch (err) {
      setError(err?.code || err?.message || 'profile_update_failed');
    } finally {
      setSavingProfile(false);
    }
  }

  function handlePhiAccepted() {
    setPhiRequired(false);
    if (typeof onSaved === 'function') onSaved();
  }

  return (
    <div style={panelStyle} data-testid="account-preferences">
      <div style={headStyle}>
        <strong style={{ fontSize: '14px' }}>Account preferences</strong>
        {onClose && (
          <button type="button" onClick={onClose} style={closeStyle} aria-label="Close preferences">
            ×
          </button>
        )}
      </div>

      {loading ? (
        <p style={mutedStyle}>Loading…</p>
      ) : (
        <>
          <section style={sectionBlockStyle}>
            <h3 style={sectionTitleStyle}>Profile</h3>
            <form onSubmit={handleSaveProfile} style={formStyle}>
              <label style={labelStyle}>
                Organization
                <input
                  type="text"
                  value={organization}
                  onChange={(e) => setOrganization(e.target.value)}
                  style={inputStyle}
                  autoComplete="organization"
                  data-testid="account-profile-organization"
                />
              </label>
              <label style={labelStyle}>
                Country
                <select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  style={inputStyle}
                  required
                  data-testid="account-profile-country"
                >
                  <option value="">Select country</option>
                  {PROFILE_COUNTRIES.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>
              <label style={labelStyle}>
                How do you participate?
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  style={inputStyle}
                  data-testid="account-profile-role"
                >
                  <option value="">Select seller, buyer, or both</option>
                  {PROFILE_PARTICIPANT_ROLES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <button type="submit" style={saveBtnStyle} disabled={savingProfile}>
                {savingProfile ? 'Saving…' : 'Save profile'}
              </button>
              {profileSaved && <p style={okStyle}>Profile saved.</p>}
            </form>
          </section>

          <section style={sectionBlockStyle}>
            <h3 style={sectionTitleStyle}>Data-entry attestation</h3>
            <PhiAttestationSection
              required={phiRequired}
              onAccepted={handlePhiAccepted}
            />
          </section>

          <section style={sectionBlockStyle}>
            <h3 style={sectionTitleStyle}>Communications</h3>
            <label style={checkRowStyle}>
              <input
                type="checkbox"
                checked={marketing}
                disabled={savingMarketing}
                onChange={(e) => handleMarketingToggle(e.target.checked)}
                data-testid="marketing-consent-toggle"
              />
              <span>
                Send me occasional updates about new LooporaData features, the Marketplace, and
                product offerings. You can unsubscribe anytime.
              </span>
            </label>
            <p style={mutedStyle}>Consent version {MARKETING_CONSENT_VERSION}</p>
          </section>
        </>
      )}
      {error && <p style={errorStyle}>{error}</p>}
    </div>
  );
}

const panelStyle = {
  padding: '10px 12px 12px',
  minWidth: '300px',
  maxWidth: '360px',
  maxHeight: '70vh',
  overflowY: 'auto',
};

const headStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '8px',
};

const closeStyle = {
  border: 'none',
  background: 'transparent',
  fontSize: '18px',
  lineHeight: 1,
  cursor: 'pointer',
  color: 'var(--muted)',
};

const sectionBlockStyle = {
  marginTop: '12px',
  paddingTop: '12px',
  borderTop: '1px solid var(--line)',
};

const sectionTitleStyle = {
  margin: '0 0 8px',
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--ink)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const formStyle = { display: 'flex', flexDirection: 'column', gap: '10px' };
const labelStyle = { display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: 'var(--ink2)' };
const inputStyle = {
  height: '36px',
  padding: '0 10px',
  border: '1px solid var(--line)',
  borderRadius: '4px',
  fontSize: '13px',
  fontFamily: 'var(--font)',
  color: 'var(--ink)',
  background: 'var(--surface)',
  width: '100%',
  boxSizing: 'border-box',
};

const checkRowStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '10px',
  fontSize: '12px',
  color: 'var(--ink2)',
};

const saveBtnStyle = {
  alignSelf: 'flex-start',
  padding: '8px 14px',
  borderRadius: '999px',
  border: 'none',
  background: 'var(--accent)',
  color: '#fff',
  fontSize: '12px',
  cursor: 'pointer',
  fontFamily: 'var(--font)',
};

const mutedStyle = { fontSize: '11px', color: 'var(--muted)', margin: '8px 0 0' };
const errorStyle = { fontSize: '12px', color: 'var(--error)', margin: '8px 0 0' };
const okStyle = { fontSize: '12px', color: 'var(--ink2)', margin: '4px 0 0' };
